import React, { useState } from 'react';
import Input from './Input';
import { cx } from './cx';
import { focusRing } from './focusRing';
import { playClickSound } from '@/utils/soundUtils';

export interface AmountInputProps {
  value: string;
  onChange: (value: string) => void;
  currency?: string;
  /** Quick chips never push the value above this. */
  max?: number;
  quickAmounts?: number[];
  /** `add` chips add to the value; `set` chips replace it. */
  quickMode?: 'add' | 'set';
  invalid?: boolean;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  /** Number format; defaults to en-US like Money ($1,234 in every UI language). */
  locale?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Read a complete amount: digits and one decimal point, at most two decimals.
 * A comma is the decimal mark when there is no dot ("12,50" → "12.50") unless
 * exactly three digits follow it ("1,000" → "1000"); with both, the last one is
 * the decimal mark ("1.234,56" and "1,234.56" → "1234.56"). Apply it to the whole
 * typed text, never keystroke by keystroke (AmountInput keeps the raw text while
 * the field is focused), or "1," would turn into a decimal before "000" arrives.
 */
export function sanitizeAmount(raw: string): string {
  let text = raw.replace(/[^0-9.,]/g, '');
  const lastDot = text.lastIndexOf('.');
  const lastComma = text.lastIndexOf(',');
  if (lastComma !== -1 && lastDot !== -1) {
    text = lastComma > lastDot ? text.replace(/\./g, '').replace(',', '.') : text.replace(/,/g, '');
  } else if (lastComma !== -1) {
    const parts = text.split(',');
    // A single comma followed by exactly three digits is a thousands separator ("1,234").
    text = parts.length === 2 && parts[1].length === 3 ? parts.join('') : `${parts[0]}.${parts.slice(1).join('')}`;
  }
  text = text.replace(/,/g, '');
  const dot = text.indexOf('.');
  if (dot === -1) return text;
  const whole = text.slice(0, dot);
  const frac = text.slice(dot + 1).replace(/\./g, '').slice(0, 2);
  return `${whole}.${frac}`;
}

function currencySymbol(locale: string, currency: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? '$';
  } catch {
    return '$';
  }
}

/** Money amount field with optional quick-amount chips. */
export default function AmountInput({
  value,
  onChange,
  currency = 'USD',
  max,
  quickAmounts,
  quickMode = 'add',
  invalid,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
  locale,
  disabled,
  className,
}: AmountInputProps) {
  // Same as Money: dollars read $1,234 in every UI language (typing still accepts comma decimals).
  const loc = locale ?? 'en-US';
  const whole = new Intl.NumberFormat(loc, { style: 'currency', currency, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0 });
  // What the player typed, kept while it still means the current value; a value the
  // host sets (quick chips, clamping to a maximum) replaces it, and blur shows the value.
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft !== null && sanitizeAmount(draft) === value ? draft : value;
  return (
    <div className={cx('flex min-w-0 flex-col gap-3', className)}>
      <Input
        id={id}
        inputMode="decimal"
        autoComplete="off"
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        invalid={invalid}
        disabled={disabled}
        inputSize="lg"
        value={shown}
        placeholder="0.00"
        onChange={(e) => {
          const raw = e.target.value.replace(/[^0-9.,]/g, '');
          setDraft(raw);
          onChange(sanitizeAmount(raw));
        }}
        onBlur={() => setDraft(null)}
        leading={<span className="font-money text-body font-semibold text-secondary">{currencySymbol(loc, currency)}</span>}
        className="font-money font-semibold"
      />
      {quickAmounts && quickAmounts.length > 0 ? (
        <div className="grid grid-cols-3 gap-2 @bp-sm:grid-cols-5">
          {quickAmounts.map((amount) => {
            const label = (quickMode === 'add' ? '+' : '') + whole.format(amount);
            return (
              <button
                key={amount}
                type="button"
                disabled={disabled}
                onClick={() => {
                  playClickSound();
                  const base = quickMode === 'add' ? Number(value || 0) : 0;
                  let next = base + amount;
                  if (max != null) next = Math.min(next, max);
                  onChange(String(Math.round(next * 100) / 100));
                }}
                className={cx(
                  'h-11 rounded-button border border-btn-secondary-edge bg-btn-secondary font-money text-small font-semibold text-primary transition-colors hover:bg-action-neutral-hover disabled:text-disabled',
                  focusRing
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
